import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const RenameModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  itemName = '',
  itemType = 'item',
  loading = false 
}) => {
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNewName(itemName);
      setError('');
    }
  }, [isOpen, itemName]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!newName?.trim()) {
      setError('Name cannot be empty');
      return;
    }

    if (newName?.trim() === itemName?.trim()) {
      setError('Please enter a different name');
      return;
    }

    if (newName?.trim()?.length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }

    if (newName?.trim()?.length > 100) {
      setError('Name must be less than 100 characters');
      return;
    }

    setError('');
    onConfirm(newName?.trim());
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Escape' && !loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={!loading ? onClose : undefined} />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Edit2" size={20} className="text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">
                Rename {itemType}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
              disabled={loading}
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                {itemType} Name
              </label>
              <Input
                type="text"
                value={newName}
                onChange={(e) => {
                  setNewName(e?.target?.value);
                  if (error) setError('');
                }}
                onKeyDown={handleKeyDown}
                placeholder={`Enter ${itemType?.toLowerCase()} name`}
                disabled={loading}
                autoFocus
                className={error ? 'border-error focus:ring-error' : ''}
              />
              {error && (
                <p className="text-error text-sm mt-1 flex items-center gap-1">
                  <Icon name="AlertCircle" size={14} />
                  {error}
                </p>
              )}
            </div>

            <div className="text-sm text-muted-foreground mb-6">
              <p className="flex items-center gap-2">
                <Icon name="Info" size={14} />
                Current name: <span className="font-medium">{itemName}</span>
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={loading}
                iconName={loading ? 'Loader' : 'Save'}
                iconClassName={loading ? 'animate-spin' : ''}
              >
                Rename
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default RenameModal;